import { zodResolver } from "@hookform/resolvers/zod";
import LoadingButton from "@mui/lab/LoadingButton";
import { Divider, Grid, Skeleton, TextField, Typography } from "@mui/material";
import { t } from "i18next";
import { useForm } from "react-hook-form";
import { z } from "zod";
import BasicDialog from "../../components/base/BasicDialog";
import { ControlledSelect } from "../../components/form/ControlledSelect/ControlledSelect";
import { ControlledTextField } from "../../components/form/ControlledTextField/ControlledTextField";
// import { useFormClientSideErrorsHandler, useFormServerSideErrorsHandler } from '../../components/form/ErrorsHandler/ErrorsHandler';
import { COMPANY_ROLE_CHOICES } from "../../helpers/constants";
import { useToasts } from "../../hooks/useToasts";
import { useEditPersonMutation, useGetPersonDetails } from "../../services/personRepositoryService";

export const editConnectedPersonFormSchema = z.object({
    name: z.string().min(1, t("persons.validation.nameRequired")),
    surname: z.string().min(1, t("persons.validation.surnameRequired")),
    role: z.string().optional(),
    connectedCompany: z.string().optional(),
    companyFunction: z.string().optional(),
    phone: z.string().optional(),
    email: z.union([z.string().email(t("persons.validation.invalidEmail")), z.literal("")]),
});

export function EditConnectedPersonDialog(props) {
    const { data: personDetails, isLoading, isFetching } = useGetPersonDetails(props.personId);
    const { mutate: editPerson, isPending } = useEditPersonMutation();
    // const { onServerError } = useFormServerSideErrorsHandler()
    const { error, success } = useToasts();

    const onSubmit = (data) => {
        console.log(data);
        editPerson(
            {
                email: data.email || "",
                name: data.name || "",
                personId: props.personId,
                phone: data.phone || "",
                surname: data.surname || "",
            },
            {
                onError: (v) => error(v.message),
                onSuccess: () => {
                    success(t("persons.editConnectedPerson.personUpdatedSuccessfully"));
                    props.onClose();
                    props.onSuccessEdit?.();
                },
            },
        );
    };

    return (
        <BasicDialog
            open={props.open}
            onClose={props.onClose}
            titleAlign={"center"}
            contentAlign={"center"}
            title={
                personDetails?.superior_in_relation
                    ? t("persons.editConnectedPerson.relatedPerson")
                    : t("persons.editConnectedPerson.person")
            }
            maxWidth={"xs"}
            showDialogActions
            titleSx={{
                pb: 0,
            }}
            headerComponent={
                <Typography
                    variant={"subtitle1"}
                    sx={{ color: "text.secondary", mx: "auto", mb: 1 }}
                >
                    {t("persons.editConnectedPerson.editPersonData")}
                </Typography>
            }
        >
            {isLoading || isFetching || !personDetails ? (
                <Grid container direction={"column"} gap={2}>
                    <Skeleton variant="rounded" width={"100%"} height={50} />
                    <Skeleton variant="rounded" width={"100%"} height={50} />
                    <Skeleton variant="rounded" width={"100%"} height={50} />
                    <Skeleton variant="rounded" width={"100%"} height={50} />
                    <Skeleton variant="rounded" width={"100%"} height={50} />
                    <Skeleton variant="rounded" width={"100%"} height={50} />
                    <Skeleton variant="rounded" width={"100%"} height={50} />
                </Grid>
            ) : (
                <EditConnectedPersonForm
                    isBtnLoading={isPending}
                    onSubmit={onSubmit}
                    defaultValues={{
                        name: personDetails.name || "",
                        surname: personDetails.surname || "",
                        phone: personDetails.phone.phone_number || "",
                        email: personDetails.email.email_address || "",
                        role: personDetails.relation_role || "",
                        companyFunction: personDetails.company_role || "",
                        connectedCompany: personDetails.related_companies[0]?.company_name || "",
                    }}
                    superiorInRelation={personDetails.superior_in_relation}
                />
            )}
        </BasicDialog>
    );
}

export function EditConnectedPersonForm(props) {
    const { handleSubmit, control } = useForm({
        resolver: zodResolver(editConnectedPersonFormSchema),
        defaultValues: props.defaultValues,
    });
    // const { onError } = useFormClientSideErrorsHandler()
    const { onFormErrors } = useToasts();

    return (
        <>
            <Grid container direction={"column"}>
                {props.superiorInRelation && (
                    <>
                        <Grid container direction={"column"}>
                            <Grid item sx={{ mb: 2 }}>
                                <Typography
                                    variant={"body1"}
                                    sx={{
                                        fontWeight: "medium",
                                        textAlign: "start",
                                    }}
                                >
                                    {t("persons.editConnectedPerson.relatedTo")}
                                </Typography>
                            </Grid>
                            <Grid container direction={"row"} gap={1}>
                                <Grid item xs>
                                    <TextField
                                        label={t("persons.editConnectedPerson.firstName")}
                                        size="small"
                                        disabled={true}
                                        value={props.superiorInRelation.name}
                                    />
                                </Grid>
                                <Grid item xs>
                                    <TextField
                                        label={t("persons.editConnectedPerson.lastName")}
                                        size="small"
                                        disabled={true}
                                        value={props.superiorInRelation.surname}
                                    />
                                </Grid>
                                {/* <IconButton color='primary' onClick={() => {
                                    // openEditConnectedPersonDialog({
                                    //     Component: EditConnectedPersonDialog,
                                    //     componentProps: {
                                    //     }
                                    // })
                                }}>
                                    <BadgeIcon />
                                </IconButton> */}
                            </Grid>

                            {/* <Button variant='outlined' sx={{ mt: 2 }}>
                        {t("persons.editConnectedPerson.changeRelation")}
                    </Button> */}
                        </Grid>
                        <Divider sx={{ mt: 2, mb: 2 }} />
                    </>
                )}
                <Grid container direction={"column"} spacing={2}>
                    <Grid item>
                        <Typography
                            variant={"body1"}
                            sx={{ fontWeight: "medium", textAlign: "start" }}
                        >
                            {t("persons.editConnectedPerson.personData")}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <ControlledTextField
                            control={control}
                            name="name"
                            label={t("persons.editConnectedPerson.firstName")}
                        />
                    </Grid>
                    <Grid item>
                        <ControlledTextField
                            control={control}
                            name="surname"
                            label={t("persons.editConnectedPerson.lastName")}
                        />
                    </Grid>
                    {props.superiorInRelation && (
                        <Grid item>
                            <ControlledTextField
                                control={control}
                                name="role"
                                label={t("persons.editConnectedPerson.role")}
                            />
                        </Grid>
                    )}
                    <Grid item>
                        <ControlledTextField
                            control={control}
                            name="connectedCompany"
                            label={t("persons.editConnectedPerson.relatedCompany")}
                            props={{ disabled: true }}
                        />
                    </Grid>
                    <Grid item>
                        <ControlledSelect
                            disabled={!props.defaultValues.connectedCompany}
                            control={control}
                            data={COMPANY_ROLE_CHOICES.map((role) => ({
                                label: t(`company.roles.${role}`),
                                value: role,
                            }))}
                            label={t("persons.editConnectedPerson.function")}
                            name="companyFunction"
                        />
                    </Grid>
                    <Grid item>
                        <ControlledTextField
                            control={control}
                            name="phone"
                            label={t("persons.editConnectedPerson.phoneNumber")}
                        />
                    </Grid>
                    <Grid item>
                        <ControlledTextField
                            control={control}
                            name="email"
                            label={t("persons.editConnectedPerson.email")}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <LoadingButton
                loading={props.isBtnLoading}
                fullWidth
                onClick={handleSubmit(props.onSubmit, onFormErrors)}
                variant="contained"
                sx={{ mt: 4 }}
            >
                {t("persons.editConnectedPerson.saveChanges")}
            </LoadingButton>
        </>
    );
}
